import { isPlatformBrowser } from "@angular/common";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { inject, Injectable, PLATFORM_ID } from "@angular/core";
import { OrderTicket } from "@models/order";
import { Observable } from "rxjs";
import { environment } from "../../environments/environment";
import { LocalStorageService } from "./local-storage.service";
import {
  BatchScheduleProposal,
  BatchScheduleConfirmation,
} from "./types/batch-schedule-proposal.type";
import {
  TicketBatchRequestBody,
  UpdateTicketBatchRequestBody,
  UpdateTicketRequestBody,
} from "./types/ticket-batch-request.type";
import {
  TicketBatchResponseBody,
  TicketResponseBody,
} from "./types/ticket-batch-response.type";

export interface RegisterTicketOwnerRequest {
  user_email: string;
  user_name: string;
  user_last_name: string;
  user_cpf?: string | null;
  user_identity_card?: string | null;
}

export interface TransferTicketOwnerRequest {
  user_email: string;
  user_phone_number: string;
}

@Injectable({
  providedIn: "root",
})
export class TicketManagementService {
  private readonly apiUrlEvents = environment.API_BACKEND;

  constructor(
    private readonly http: HttpClient,
    private localStorageService: LocalStorageService,
  ) {}

  platformId = inject(PLATFORM_ID);

  private getToken(): string | null {
    if (isPlatformBrowser(this.platformId)) {
      return this.localStorageService.getItem("auth-token");
    }
    return null;
  }

  private getHttpHeaders(): HttpHeaders {
    const token = this.getToken();
    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });
  }

  createTicketBatch(
    eventId: number,
    createTicketBatchRequestBody: TicketBatchRequestBody,
  ): Observable<TicketBatchResponseBody> {
    const payload: TicketBatchRequestBody = {
      sale_start: createTicketBatchRequestBody.sale_start,
      sale_end: createTicketBatchRequestBody.sale_end,
      transition_mode:
        createTicketBatchRequestBody.transition_mode ?? "date_or_sold_out",
      absorbs_platform_fee: createTicketBatchRequestBody.absorbs_platform_fee,
      absorbs_processing_fee:
        createTicketBatchRequestBody.absorbs_processing_fee,
      sold_at_pdv: createTicketBatchRequestBody.sold_at_pdv,
      has_half_price: !!createTicketBatchRequestBody.has_half_price,
      tickets: (createTicketBatchRequestBody.tickets ?? []).filter(
        (ticket) => !ticket.half_price,
      ),
    };
    return this.http.post<TicketBatchResponseBody>(
      `${this.apiUrlEvents}/events/${eventId}/batches`,
      payload,
      { headers: this.getHttpHeaders() },
    );
  }

  updateBatch(
    batchId: number,
    updateTicketBatchRequestBody: UpdateTicketBatchRequestBody,
  ): Observable<TicketBatchResponseBody> {
    return this.http.patch<TicketBatchResponseBody>(
      `${this.apiUrlEvents}/batches/${batchId}`,
      updateTicketBatchRequestBody,
      { headers: this.getHttpHeaders() },
    );
  }

  transitionBatch(
    batchId: number,
    options?: { ticketIds?: number[] },
  ): Observable<{ batches: TicketBatchResponseBody[] }> {
    const body: { confirmed: true; ticket_ids?: number[] } = {
      confirmed: true,
    };
    if (options?.ticketIds?.length) {
      body.ticket_ids = options.ticketIds;
    }
    return this.http.post<{ batches: TicketBatchResponseBody[] }>(
      `${this.apiUrlEvents}/batches/${batchId}/transition`,
      body,
      { headers: this.getHttpHeaders() },
    );
  }

  previewBatchSchedule(
    batchId: number,
    attributes: UpdateTicketBatchRequestBody,
    preserveDuration = false,
  ): Observable<BatchScheduleProposal> {
    return this.http.post<BatchScheduleProposal>(
      `${this.apiUrlEvents}/batches/${batchId}/schedule_preview`,
      { ...attributes, preserve_duration: preserveDuration },
      { headers: this.getHttpHeaders() },
    );
  }

  confirmBatchSchedule(
    batchId: number,
    token: string,
  ): Observable<BatchScheduleConfirmation> {
    return this.http.post<BatchScheduleConfirmation>(
      `${this.apiUrlEvents}/batches/${batchId}/schedule_confirmation`,
      { token },
      { headers: this.getHttpHeaders() },
    );
  }

  createTicketInBatch(
    batchId: number,
    createTicketInBatchRequestBody: UpdateTicketRequestBody,
  ): Observable<TicketResponseBody> {
    return this.http.post<TicketResponseBody>(
      `${this.apiUrlEvents}/batches/${batchId}/tickets`,
      createTicketInBatchRequestBody,
      { headers: this.getHttpHeaders() },
    );
  }

  updateTicketInBatch(
    batchId: number,
    ticketId: number,
    updateTicketRequestBody: UpdateTicketRequestBody,
  ): Observable<TicketResponseBody> {
    return this.http.patch<TicketResponseBody>(
      `${this.apiUrlEvents}/batches/${batchId}/tickets/${ticketId}`,
      updateTicketRequestBody,
      { headers: this.getHttpHeaders() },
    );
  }

  deleteBatch(batchId: number) {
    return this.http.delete<{ message: string }>(
      `${this.apiUrlEvents}/batches/${batchId}`,
      { headers: this.getHttpHeaders() },
    );
  }

  deleteTicketInBatch(batchId: number, ticketId: number) {
    return this.http.delete<{ message: string }>(
      `${this.apiUrlEvents}/batches/${batchId}/tickets/${ticketId}`,
      { headers: this.getHttpHeaders() },
    );
  }

  registerTicketOwner(ticket_id: number, request: RegisterTicketOwnerRequest) {
    return this.http.put<any>(
      `${this.apiUrlEvents}/my_tickets/${ticket_id}`,
      request,
      {
        headers: this.getHttpHeaders(),
      },
    );
  }

  getTicketWithQrCode(ticket_id: number) {
    return this.http.get<OrderTicket>(
      `${this.apiUrlEvents}/my_tickets/${ticket_id}/qr_code`,
    );
  }

  transferTicket(ticket_id: number, request: TransferTicketOwnerRequest) {
    return this.http.put<TransferTicketOwnerRequest>(
      `${this.apiUrlEvents}/my_tickets/${ticket_id}/transfer`,
      request,
      { headers: this.getHttpHeaders() },
    );
  }
}
